import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModelUseType, PromptTypeType } from '../../../types/backend';
import { usePromptService } from '../services/promptService';
import { detectPromptPlaceholders, detectTemplateVariables } from '../utils/promptUtils';

export interface PreviewPlaceholder {
  name: string;
  /** {{name}} agent-runtime variable (substituted client-side) vs {name} invoke-time placeholder */
  agent: boolean;
  token: string;
}

export const usePromptPreview = (
  content: string,
  customerId: string | undefined,
  promptType?: PromptTypeType,
  modelUse?: ModelUseType,
) => {
  const promptService = usePromptService();
  const [values, setValues] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [contractChecked, setContractChecked] = useState<string | null>(null);
  const [contractErrors, setContractErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const placeholders = useMemo<PreviewPlaceholder[]>(() => {
    const singles = detectPromptPlaceholders(content).map((name) => ({
      name,
      agent: false,
      token: `{${name}}`,
    }));
    const agents = detectTemplateVariables(content).map((name) => ({
      name,
      agent: true,
      token: `{{${name}}}`,
    }));
    return [...singles, ...agents];
  }, [content]);

  // What the Studio already knows, so the usual case needs no typing. Only the customer
  // in context: every other placeholder is the operator's to supply, and a sample this
  // pane invented would read on screen exactly like one taken from a real event.
  const contextValues = useMemo<Record<string, string>>(
    () => (customerId ? { '{customer_id}': customerId } : ({} as Record<string, string>)),
    [customerId],
  );

  /** The value a placeholder runs with: what was typed, else what context supplies. */
  const valueFor = useCallback(
    (token: string) => values[token] ?? contextValues[token] ?? '',
    [values, contextValues],
  );

  /** Whether this token is showing a context value nobody has typed over. */
  const isFromContext = useCallback(
    (token: string) => values[token] === undefined && contextValues[token] !== undefined,
    [values, contextValues],
  );

  const setValue = useCallback((token: string, value: string) => {
    setValues((prev) => ({ ...prev, [token]: value }));
  }, []);

  // Typed values are dropped when the customer changes. They are keyed by token alone,
  // so a customer id typed for one customer would otherwise still be sent as the
  // variable while a different customer authorises the run.
  useEffect(() => {
    setValues({});
  }, [customerId]);

  const run = useCallback(async () => {
    if (!customerId) {
      setError('A customer context is required to test prompts');
      return;
    }
    if (!content.trim()) {
      setError('Prompt content is empty — nothing to test');
      return;
    }

    setRunning(true);
    setError(null);
    setOutput(null);
    setWarnings([]);
    setContractChecked(null);
    setContractErrors([]);

    try {
      // {{agent}} variables are substituted here, mirroring the agent runtime;
      // {single} placeholders are rendered server-side like production paths.
      let resolvedContent = content;
      const clientWarnings: string[] = [];
      for (const p of placeholders) {
        if (!p.agent) continue;
        const value = valueFor(p.token);
        if (value) {
          resolvedContent = resolvedContent.split(p.token).join(value);
        } else {
          clientWarnings.push(`${p.token} had no value provided`);
        }
      }

      const variables = Object.fromEntries(
        placeholders
          .filter((p) => !p.agent)
          .map((p) => [p.name, valueFor(p.token)]),
      );
      const result = await promptService.testPrompt({
        customer_id: customerId,
        content: resolvedContent,
        variables,
        // A typed prompt (e.g. enrichment) is run and judged the way the pipeline
        // runs it, so a draft that would be rejected in production is rejected here.
        prompt_type: promptType,
        // The pipeline use whose bound model runs the test (PS-UC-17).
        model_use: modelUse,
      });
      setOutput(result.output);
      setWarnings([...clientWarnings, ...(result.warnings ?? [])]);
      setContractChecked(result.contract_checked ?? null);
      setContractErrors(result.contract_errors ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test prompt');
    } finally {
      setRunning(false);
    }
  }, [promptService, customerId, content, placeholders, valueFor, promptType, modelUse]);

  return {
    placeholders,
    valueFor,
    isFromContext,
    setValue,
    run,
    running,
    output,
    warnings,
    contractChecked,
    contractErrors,
    error,
    setError,
  };
};
